import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ReportsService } from '../../../reports.service';

export interface GlobalFilterColumnOption {
  key: string;
  label: string;
  tableName: string;
  columnName: string;
}

export interface GlobalFilterCondition {
  id: string;
  columnKey: string | null;
  selectedValues: string[];
  availableValues: string[];
  valuesLoading: boolean;
  searchTerm: string;
  // Cached (not computed in the template) - columns already used by OTHER conditions are excluded.
  availableColumns: GlobalFilterColumnOption[];
}

let conditionSequence = 0;

/**
 * "Manage global filter" popup body - a chip per condition (column + its selected values), with the
 * active condition's column picker + distinct-value multiselect (via getValuesByColumnNames) below.
 */
@Component({
  selector: 'app-global-filter-modal',
  standalone: false,
  templateUrl: './global-filter-modal.component.html',
  styleUrl: './global-filter-modal.component.scss'
})
export class GlobalFilterModalComponent implements OnChanges {
  @Input() columnOptions: GlobalFilterColumnOption[] = [];
  @Output() cancelled = new EventEmitter<void>();
  @Output() conditionsSaved = new EventEmitter<GlobalFilterCondition[]>();

  conditions: GlobalFilterCondition[] = [];
  activeConditionId: string | null = null;

  constructor(private reportsService: ReportsService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['columnOptions']) {
      this.refreshAvailableColumns();
    }
  }

  // app-modal keeps this component mounted between opens - the host calls this before showing again.
  // Defaults to one empty condition so the column dropdown + values section is visible immediately,
  // instead of requiring an "Add condition" click first.
  resetState(initialConditions: GlobalFilterCondition[] = []): void {
    this.conditions = initialConditions.length
      ? initialConditions.map(condition => ({ ...condition, selectedValues: [...condition.selectedValues] }))
      : [this.createCondition()];
    this.activeConditionId = this.conditions[0]?.id || null;
    this.refreshAvailableColumns();
  }

  get activeCondition(): GlobalFilterCondition | undefined {
    return this.conditions.find(condition => condition.id === this.activeConditionId);
  }

  // Columns already used by OTHER conditions can't be picked again ("not same" column requirement).
  // Recomputed for every condition whenever membership/selection changes - never call from the template.
  private refreshAvailableColumns(): void {
    this.conditions.forEach(condition => {
      const usedKeys = new Set(
        this.conditions.filter(other => other !== condition && other.columnKey).map(other => other.columnKey)
      );
      condition.availableColumns = this.columnOptions.filter(option => !usedKeys.has(option.key));
    });
  }

  getColumnLabel(condition: GlobalFilterCondition): string {
    return this.columnOptions.find(option => option.key === condition.columnKey)?.label || 'Select column';
  }

  addCondition(): void {
    const condition = this.createCondition();
    this.conditions = [...this.conditions, condition];
    this.activeConditionId = condition.id;
    this.refreshAvailableColumns();
  }

  private createCondition(): GlobalFilterCondition {
    conditionSequence += 1;
    return {
      id: `condition-${conditionSequence}`,
      columnKey: null,
      selectedValues: [],
      availableValues: [],
      valuesLoading: false,
      searchTerm: '',
      availableColumns: []
    };
  }

  selectCondition(condition: GlobalFilterCondition): void {
    this.activeConditionId = condition.id;
  }

  removeCondition(condition: GlobalFilterCondition): void {
    this.conditions = this.conditions.filter(item => item.id !== condition.id);
    if (this.activeConditionId === condition.id) {
      this.activeConditionId = this.conditions[0]?.id || null;
    }
    this.refreshAvailableColumns();
  }

  onColumnSelected(condition: GlobalFilterCondition, key: string): void {
    condition.columnKey = key;
    condition.selectedValues = [];
    condition.availableValues = [];
    condition.searchTerm = '';
    this.refreshAvailableColumns();
    const option = this.columnOptions.find(item => item.key === key);
    if (!option) {
      return;
    }
    condition.valuesLoading = true;
    this.reportsService.getValuesByColumnNames({
      tableName: option.tableName,
      columnNames: [option.columnName],
      distinct: true
    }).subscribe({
      next: (response: any) => {
        if (condition.columnKey !== key) { return; }
        condition.valuesLoading = false;
        condition.availableValues = Array.isArray(response) ? response : (response?.values || []);
      },
      error: () => {
        if (condition.columnKey !== key) { return; }
        condition.valuesLoading = false;
        condition.availableValues = [];
      }
    });
  }

  filteredValues(condition: GlobalFilterCondition): string[] {
    const term = (condition.searchTerm || '').trim().toLowerCase();
    if (!term) { return condition.availableValues; }
    return condition.availableValues.filter(value => value.toLowerCase().includes(term));
  }

  isValueSelected(condition: GlobalFilterCondition, value: string): boolean {
    return condition.selectedValues.includes(value);
  }

  toggleValue(condition: GlobalFilterCondition, value: string): void {
    const idx = condition.selectedValues.indexOf(value);
    if (idx >= 0) { condition.selectedValues.splice(idx, 1); } else { condition.selectedValues.push(value); }
  }

  onCancel(): void {
    this.cancelled.emit();
  }

  onApply(): void {
    this.conditionsSaved.emit(this.conditions.map(condition => ({ ...condition })));
  }
}
